import { Button } from "@/components/ui/button";
import { formatCurrency } from "@/lib/currencies/currenciesStore";
import { paymentMethodLabel, paymentMethodUsesBank, type ExpenseRecord } from "@/lib/expenses/expensesStore";
import { cn } from "@/lib/utils";
import { format, isValid, parseISO } from "date-fns";
import { Banknote, CalendarIcon, CreditCard, Edit, FileText, Tags, Trash2, Wallet } from "lucide-react";

export type ExpenseDisplayLabels = {
  subcategory: string;
  category: string;
};

type ExpenseItemCardProps = {
  expense: ExpenseRecord;
  labels: ExpenseDisplayLabels;
  variant: "grid" | "list";
  onEdit?: () => void;
  onDelete?: () => void;
};

function formatExpenseDate(raw: string): string {
  const parsed = parseISO(raw);
  if (!isValid(parsed)) return raw || "Date not set";
  return format(parsed, "d MMM yyyy");
}

function paymentDisplay(expense: ExpenseRecord): string {
  const method = expense.paymentMethodLabel || paymentMethodLabel(expense.paymentMethod);
  const extras = [
    expense.bankName || null,
    expense.chequeNo ? `Cheque ${expense.chequeNo}` : null,
    expense.referenceNo || null,
  ].filter(Boolean);
  return extras.length ? `${method} · ${extras.join(" · ")}` : method;
}

function Field({
  label,
  value,
  icon: Icon,
  iconClassName,
}: {
  label: string;
  value: string;
  icon: typeof Tags;
  iconClassName?: string;
}) {
  return (
    <div className="flex gap-2.5 items-start min-w-0">
      <Icon className={cn("w-4 h-4 shrink-0 mt-0.5", iconClassName)} strokeWidth={2} />
      <div className="min-w-0">
        <div className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">{label}</div>
        <div className="font-semibold text-sm text-foreground break-words">{value}</div>
      </div>
    </div>
  );
}

function Actions({ onEdit, onDelete }: Pick<ExpenseItemCardProps, "onEdit" | "onDelete">) {
  if (!onEdit && !onDelete) return null;
  return (
    <div className="flex items-center gap-2 shrink-0">
      {onEdit ? (
        <Button type="button" variant="outline" size="sm" className="gap-1.5 h-9 px-3 text-xs font-medium" onClick={onEdit}>
          <Edit className="w-3.5 h-3.5" />
          Edit
        </Button>
      ) : null}
      {onDelete ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-9 w-9 p-0 border-destructive/60 text-destructive hover:bg-destructive/10 hover:text-destructive"
          onClick={onDelete}
          aria-label="Delete expense"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      ) : null}
    </div>
  );
}

export function ExpenseItemCard({ expense, labels, variant, onEdit, onDelete }: ExpenseItemCardProps) {
  const amount = formatCurrency(Number.isFinite(expense.amount) ? expense.amount : 0);
  const date = formatExpenseDate(expense.date);
  const payment = paymentDisplay(expense);
  const description = (expense.description ?? "").trim();
  const PaymentIcon = paymentMethodUsesBank(expense.paymentMethod) ? CreditCard : Wallet;

  if (variant === "list") {
    return (
      <div className="rounded-lg border border-border bg-card p-4 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0 flex-1 space-y-3">
            <div>
              <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Subcategory</p>
              <h3 className="text-base font-semibold text-foreground break-words">{labels.subcategory}</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-x-6 gap-y-3">
              <Field label="Category" value={labels.category} icon={Tags} iconClassName="text-indigo-600" />
              <Field label="Amount" value={amount} icon={Banknote} iconClassName="text-emerald-600" />
              <Field label="Date" value={date} icon={CalendarIcon} iconClassName="text-sky-600" />
              <Field label="Payment" value={payment} icon={PaymentIcon} iconClassName="text-amber-600" />
            </div>
            {description ? (
              <Field label="Description" value={description} icon={FileText} iconClassName="text-muted-foreground" />
            ) : null}
          </div>
          <Actions onEdit={onEdit} onDelete={onDelete} />
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-border bg-card text-card-foreground overflow-hidden shadow-sm h-full flex flex-col">
      <div className="h-1 w-full bg-primary" aria-hidden />
      <div className="p-4 pt-5 flex flex-col gap-4 flex-1">
        <div className="space-y-1">
          <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Subcategory</p>
          <h3 className="text-lg font-bold tracking-tight text-foreground leading-snug break-words">{labels.subcategory}</h3>
        </div>

        <div className="grid grid-cols-1 gap-3">
          <Field label="Category" value={labels.category} icon={Tags} iconClassName="text-indigo-600" />
          <Field label="Amount" value={amount} icon={Banknote} iconClassName="text-emerald-600" />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Date" value={date} icon={CalendarIcon} iconClassName="text-sky-600" />
            <Field label="Payment" value={payment} icon={PaymentIcon} iconClassName="text-amber-600" />
          </div>
          {description ? (
            <Field label="Description" value={description} icon={FileText} iconClassName="text-muted-foreground" />
          ) : null}
        </div>

        <div className="mt-auto pt-1">
          <Actions onEdit={onEdit} onDelete={onDelete} />
        </div>
      </div>
    </div>
  );
}
